import React, { useEffect, useState } from 'react';
import { HERO, IMAGES, BOOK } from '../data/mock';
import { ChevronDown, PlayCircle } from 'lucide-react';

const Hero = ({ onBookClick, onWatchClick }) => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 120);
    return () => clearTimeout(t);
  }, []);

  return (
    <section id="top" className="relative w-full h-[100svh] min-h-[640px] overflow-hidden">
      {/* Background image */}
      <div
        role="img"
        aria-label="ASH — candlelit Tudor witch-trial table at The Cottage, COLAB Theatre, London Bridge. The best immersive Halloween theatre experience in London 2026."
        className={`absolute inset-0 bg-cover bg-center transition-transform duration-[6000ms] ease-out ${
          loaded ? 'scale-100' : 'scale-110'
        }`}
        style={{ backgroundImage: `url(${IMAGES.hero})` }}
      />
      {/* Vignette + darkness */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/30 to-[#0a0705]" />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at 75% 55%, rgba(200,130,60,0.18) 0%, rgba(0,0,0,0) 45%), radial-gradient(ellipse at center, rgba(0,0,0,0) 40%, rgba(0,0,0,0.75) 100%)'
        }}
      />

      {/* Content */}
      <div className="relative z-10 h-full w-full flex flex-col items-center justify-center px-6 text-center">
        <div
          className={`transition-all duration-[1400ms] ${
            loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <p className="text-[10px] md:text-[11px] tracking-[0.5em] text-[#c8935a] font-fell-sc mb-8">
            {HERO.eyebrow}
          </p>

          <h1
            className="font-display font-bold text-[#f4e8ce] leading-[0.95] ember-glow"
            style={{
              fontSize: 'clamp(84px, 18vw, 260px)',
              letterSpacing: '0.1em'
            }}
          >
            <span aria-hidden="true">{HERO.title}</span>
            <span className="sr-only">
              ASH — The Witch Trials of Drudenhaus. The best immersive Halloween
              theatre experience in London 2026. A scary, most intimate witch
              trial for an audience of just two at COLAB Theatre, London Bridge.
            </span>
          </h1>

          <p className="mt-4 md:mt-6 font-fell italic text-[#d9c8a3] text-lg md:text-2xl tracking-wider">
            {HERO.tagline}
          </p>

          <div className="mt-10 divider-ornament max-w-md mx-auto">
            <span className="text-[10px] tracking-[0.4em] font-fell-sc">1627</span>
          </div>

          <p className="mt-8 font-fell italic text-[#e8dcc0] text-xl md:text-2xl max-w-2xl mx-auto">
            {HERO.question}
          </p>

          <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={onBookClick}
              data-testid="hero-cta-button"
              className="group relative inline-flex items-center gap-3 px-10 py-4 border border-[#c8935a]/60 bg-black/30 hover:bg-[#c8935a]/10 text-[#e8dcc0] hover:text-[#f4e8ce] transition-all duration-500 font-fell-sc text-[11px] tracking-[0.4em]"
            >
              <span className="relative z-10">{BOOK.soldOut ? HERO.ctaSoldOut : HERO.cta}</span>
              <span className="absolute inset-0 bg-gradient-to-r from-transparent via-[#c8935a]/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
            </button>

            {onWatchClick && (
              <button
                onClick={onWatchClick}
                aria-label="Watch the ASH teaser"
                className="group relative inline-flex items-center gap-3 px-10 py-4 border border-[#c8935a]/60 bg-black/30 hover:bg-[#c8935a]/10 text-[#e8dcc0] hover:text-[#f4e8ce] transition-all duration-500 font-fell-sc text-[11px] tracking-[0.4em]"
              >
                <PlayCircle className="w-4 h-4 text-[#c8935a] group-hover:scale-110 transition-transform relative z-10" />
                <span className="relative z-10">WATCH THE TEASER</span>
                <span className="absolute inset-0 bg-gradient-to-r from-transparent via-[#c8935a]/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-[#8b7355]">
        <span className="text-[9px] tracking-[0.4em] font-fell-sc">DESCEND</span>
        <ChevronDown className="w-4 h-4 animate-bounce" />
      </div>

      {/* Ash particles */}
      <AshParticles />
    </section>
  );
};

const AshParticles = () => {
  const particles = Array.from({ length: 24 });
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {particles.map((_, i) => {
        const left = Math.random() * 100;
        const duration = 14 + Math.random() * 18;
        const delay = Math.random() * 20;
        const size = 1 + Math.random() * 2.5;
        const opacity = 0.25 + Math.random() * 0.5;
        return (
          <span
            key={i}
            className="absolute rounded-full"
            style={{
              left: `${left}%`,
              top: '-5%',
              width: size,
              height: size,
              background: '#d9a55e',
              opacity,
              filter: 'blur(0.5px)',
              animation: `ashDrift ${duration}s linear ${delay}s infinite`
            }}
          />
        );
      })}
    </div>
  );
};

export default Hero;
